/**
 * Os risquinhos de contagem do Caderno: quatro traços e a barra que corta o
 * grupo de cinco, como se conta à mão no canto da folha.
 *
 * Servem à meta do dia (quantos de quantos), à ofensiva no cabeçalho do
 * celular e ao fim de sessão. O desenho é geometria de SVG, sem `style`
 * nenhum — cor pela folha, via `currentColor` e as classes de destaque.
 *
 * `destaque` pinta os últimos N traços de outra cor (o "bônus" que passa da
 * meta, em verde). `animar` liga a entrada um a um da sequência de 8d, pela
 * folha: cada traço lê o índice em `--i`, escrito pelo CSSOM.
 */
import { useEffect, useRef } from "react";

const PASSO = 10;
const GRUPO = 5 * PASSO + 6;

export function Risquinhos({
  quantidade,
  destaque = 0,
  animar = false,
  maximo = 15,
  rotulo,
  className,
}: {
  quantidade: number;
  destaque?: number;
  animar?: boolean;
  /** Acima disto a contagem para de desenhar: vira número ao lado. */
  maximo?: number;
  rotulo?: string;
  className?: string;
}) {
  const n = Math.max(0, Math.min(quantidade, maximo));
  const grupos = Math.ceil(n / 5);
  const largura = Math.max(PASSO, grupos * GRUPO - 6);
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    ref.current?.querySelectorAll<SVGPathElement>("path").forEach((p, i) => p.style.setProperty("--i", String(i)));
  }, [n]);

  const tracos: { d: string; bonus: boolean }[] = [];
  for (let i = 0; i < n; i += 1) {
    const g = Math.floor(i / 5);
    const k = i % 5;
    const x0 = g * GRUPO;
    const bonus = i >= n - destaque;
    if (k < 4) {
      const x = x0 + 5 + k * PASSO;
      const torto = k % 2 === 0 ? -1 : 1;
      tracos.push({ d: `M${x} ${3 + (k % 2)} L${x + torto} 29`, bonus });
    } else {
      tracos.push({ d: `M${x0} 24 L${x0 + 41} 8`, bonus });
    }
  }

  return (
    <svg
      ref={ref}
      className={["risquinhos", animar ? "risquinhos--animar" : "", className ?? ""].filter(Boolean).join(" ")}
      width={largura}
      height="30"
      viewBox={`-2 0 ${largura + 4} 30`}
      fill="none"
      {...(rotulo ? { role: "img", "aria-label": rotulo } : { "aria-hidden": true })}
    >
      {tracos.map((t, i) => (
        <path key={i} d={t.d} pathLength={100} className={t.bonus ? "risquinhos__bonus" : undefined} />
      ))}
    </svg>
  );
}
